/**
 * Trial / beta-access status helpers.
 *
 * Shared platform primitive — every Zafroi surface (ERP web, Engage,
 * the warehouse PWA) renders its own UI but reads trial status from
 * here so the contractual dates are computed once and identically.
 *
 * The end date is encoded as a fixed UTC moment so the gate behaves
 * the same regardless of the device's configured timezone.
 */

export interface TrialConfig {
  /** Tenant the trial is issued to (rendered in the banner copy). */
  tenantName: string;
  /** UTC moment after which the trial is over. */
  endDateIso: string;
  /** Customer-facing last-valid-date label, e.g. "8 June 2026". */
  endDateLabel: string;
  /** Customer-facing first-required-date label, e.g. "9 June 2026". */
  renewalDateLabel: string;
}

export interface TrialStatus {
  /** Has the trial passed its end date? */
  expired: boolean;
  /** Whole days remaining (0 = final day; -1 once expired). */
  daysRemaining: number;
}

export function getTrialStatus(
  config: TrialConfig,
  now: Date = new Date(),
): TrialStatus {
  const end = new Date(config.endDateIso);
  const msRemaining = end.getTime() - now.getTime();
  if (msRemaining <= 0) return { expired: true, daysRemaining: -1 };
  return {
    expired: false,
    daysRemaining: Math.max(0, Math.floor(msRemaining / (1000 * 60 * 60 * 24))),
  };
}
